// Exact-decimal helpers for contract money strings.
//
// The API contract (contracts/books-api.yaml `DecimalAmount`) sends money as
// decimal STRINGS, never JSON floats. All arithmetic here is exact, on bigint
// cents (scale 2). `parseFloat` is never used for arithmetic; `toChartNumber`
// exists solely to produce chart coordinates after exact math is done.

const DECIMAL_RE = /^-?(0|[1-9]\d{0,12})(\.\d{1,2})?$/;

/** Parse a contract decimal string into exact bigint cents. Throws on bad input. */
export function parseDecimal(value: string): bigint {
  if (!DECIMAL_RE.test(value)) {
    throw new Error(`invalid decimal amount: ${JSON.stringify(value)}`);
  }
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const [wholePart, fractionPart = ""] = unsigned.split(".");
  const cents =
    BigInt(wholePart ?? "0") * 100n + BigInt(fractionPart.padEnd(2, "0") || "0");
  return negative ? -cents : cents;
}

/** Exact sum of decimal strings, returned as bigint cents. */
export function sumDecimals(values: readonly string[]): bigint {
  let total = 0n;
  for (const value of values) {
    total += parseDecimal(value);
  }
  return total;
}

/** Exact delta (current - prior) of two decimal strings, as bigint cents. */
export function deltaDecimals(current: string, prior: string): bigint {
  return parseDecimal(current) - parseDecimal(prior);
}

/** Render bigint cents back to a plain contract-style decimal string ("-4342.17"). */
export function centsToDecimalString(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = abs / 100n;
  const fraction = (abs % 100n).toString().padStart(2, "0");
  return `${negative ? "-" : ""}${whole.toString()}.${fraction}`;
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export interface FormatOptions {
  /** Accounting style renders negatives in parentheses: "(4,342.17)". */
  accounting?: boolean;
}

/** Format bigint cents for display: "14,157.83" / "-4,342.17" / "(4,342.17)". */
export function formatCents(cents: bigint, options: FormatOptions = {}): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = groupThousands((abs / 100n).toString());
  const fraction = (abs % 100n).toString().padStart(2, "0");
  const magnitude = `${whole}.${fraction}`;
  if (!negative) return magnitude;
  return options.accounting ? `(${magnitude})` : `-${magnitude}`;
}

/** Format a contract decimal string for display without any float round-trip. */
export function formatAmount(value: string, options: FormatOptions = {}): string {
  return formatCents(parseDecimal(value), options);
}

/**
 * Chart coordinate for exact cents. The ONLY sanctioned decimal→float
 * conversion in this package, used after all arithmetic is complete.
 */
export function toChartNumber(cents: bigint): number {
  return Number(cents) / 100;
}

/** Chart coordinate for a contract decimal string. */
export function amountToChartNumber(value: string): number {
  return toChartNumber(parseDecimal(value));
}

/** Absolute value in bigint cents (expense magnitudes for charts/tables). */
export function absCents(cents: bigint): bigint {
  return cents < 0n ? -cents : cents;
}
